import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  ListSubheader,
  Stack,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

export default function EditUser({ user, onClose, onUpdate }) {
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [formData, setFormData] = useState({
    role_id: user?.role_id_in_team || "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) fetchRoles();
  }, [user]);

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const response = await axiosInstance.get("/api/roles");

      if (response.data.success) {
        const data =
          typeof response.data.data === "string"
            ? JSON.parse(response.data.data)
            : response.data.data;

        const activeRoles = (data.roles || []).filter((r) => r.is_active);
        setRoles(activeRoles);

        if (user?.role_id) {
          setFormData((prev) => ({ ...prev, role_id: user.role_id }));
        }
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setError("Failed to load roles");
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    if (!formData.role_id) {
      setError("Please select a role");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post("/api/permissions/assign", {
        user_id: user.user_id,
        role_id: formData.role_id,
      });

      if (response.data.success) {
        setSuccess("User role updated successfully!");
        setTimeout(() => {
          onUpdate();
        }, 1500);
      } else {
        setError(response.data.message || "Failed to update user role");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      setError(
        "Error updating user: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleTypeColor = (role) =>
    role?.is_system_role ? "success" : "info";

  const globalRoles = roles.filter((r) => r.is_system_role);
  const customRoles = roles.filter((r) => !r.is_system_role);

  const selectedRole = roles.find(
    (r) => r.id === parseInt(formData.role_id)
  );
  const currentRole = roles.find(
    (r) => r.id === user?.role_id_in_team
  );

  if (!user)
    return (
      <Box p={3} textAlign="center">
        <Typography>No user data available</Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={onClose}
          sx={{ mt: 2 }}
        >
          Close
        </Button>
      </Box>
    );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle color={'text.primary'}>Edit User Role</DialogTitle>
      <DialogContent dividers>
        {/* User Info */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color={'text.primary'}>
                  Name:
                </Typography>
                <Typography variant="body2" color={'text.primary'}>
                  {user.profile && (user.profile.fname || user.profile.lname)
                    ? `${user.profile.fname || ""} ${
                        user.profile.lname || ""
                      }`.trim()
                    : "N/A"}
                </Typography>

                <Typography variant="subtitle1" fontWeight={600} mt={1} color={'text.primary'}>
                  Email:
                </Typography>
                <Typography variant="body2">{user.user_email}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight={600} color={'text.primary'}>
                  Current Role:
                </Typography>
                {currentRole ? (
                  <Chip
                    label={`${currentRole.name.charAt(0).toUpperCase() + currentRole.name.slice(1)} (${currentRole.is_system_role ? "Global" : "Custom"})`}
                    color={getRoleTypeColor(currentRole)}
                    sx={{ mt: 1 }}
                  />
                ) : (
                  <Chip label="N/A" variant="outlined" sx={{ mt: 1 }} />
                )}

                <Typography variant="subtitle1" fontWeight={600} mt={2} color={'text.primary'}>
                  Status:
                </Typography>
                <Chip
                  label={user.verified ? "Verified" : "Pending"}
                  color={user.verified ? "success" : "warning"}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Edit Form */}
        <Box component="form" onSubmit={handleSubmit}>
               <FormControl fullWidth margin="normal" required>
            <InputLabel>Assign New Role</InputLabel>
            {loadingRoles ? (
              <Box textAlign="center" py={2}>
                <CircularProgress size={25} />
              </Box>
            ) : (
              <Select
                name="role_id"
                label="Assign New Role"
                value={formData.role_id}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                {/* Group 1: Global Roles */}
                {globalRoles.length > 0 && [
                  <ListSubheader key="global-header">
                    Global Roles (Shared)
                  </ListSubheader>,
                  globalRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name.charAt(0).toUpperCase() + role.name.slice(1)} (Global)
                    </MenuItem>
                  )),
                ]}

                {/* Group 2: Custom Roles */}
                {customRoles.length > 0 && [
                  <ListSubheader key="custom-header">
                    Custom Team Roles
                  </ListSubheader>,
                  customRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name.charAt(0).toUpperCase() + role.name.slice(1)} (Custom)
                    </MenuItem>
                  )),
                ]}
              </Select>
            )}
            <Typography variant="caption" color="text.primary" sx={{ mt: 1 }}>
              Select the new permission level for this user
            </Typography>
          </FormControl>

          {selectedRole && (
            <Alert
              severity={selectedRole.is_system_role ? "success" : "info"}
              icon={<InfoIcon />}
              sx={{ mt: 2 }}
            >
              <Typography variant="subtitle2" color={'text.primary'}>
                <strong>{selectedRole.name.charAt(0).toUpperCase() + selectedRole.name.slice(1)}:</strong>{" "}
                {selectedRole.description || "No description available"}
              </Typography>
              <Typography variant="body2" mt={1} color={'text.primary'}>
                <strong>Permissions:</strong>{" "}
                {selectedRole.permissions.length} submodule
                {selectedRole.permissions.length !== 1 ? "s" : ""}
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              icon={<ErrorIcon />}
              sx={{ mt: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{ mt: 2 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}
        </Box>

        {/* Info Section */}
        <Card variant="outlined" sx={{ mt: 3, bgcolor: "background.default" }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} mb={1} color={'text.primary'}>
              <InfoIcon fontSize="small" sx={{ mr: 1 }} />
              Role Types
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                <Chip label="Global" color="success" size="small" sx={{ mr: 1 }} />
                Standard roles shared across all teams.
              </Typography>
              <Typography variant="body2">
                <Chip label="Custom" color="info" size="small" sx={{ mr: 1 }} />
                Team-specific roles with tailored permissions.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          color="inherit"
          startIcon={<CloseIcon />}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          startIcon={isLoading ? <CircularProgress size={18} /> : <SaveIcon />}
          disabled={isLoading || loadingRoles}
        >
          {isLoading ? "Updating..." : "Update Role"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}